/**
 * The downsizing plan page (rebuild, 21 Oct 2026; the agreed mock-up's j1-hub).
 *
 * Where the module opens once a plan is chosen: the sale and the state, the chosen plan against staying put,
 * the advice document, and the next step — telling Services Australia about the sale, with the 24-month
 * exemption explained. Every figure is the chosen card's own, as chosen.
 *
 * Aged care's page, in the shared pieces (owner, 22 Oct 2026: "Yes i want this all consistent"): the chosen-plan
 * card (ChosenPlanCard), the document and the next step in one card (DocumentAndNextStep), "Model something
 * else" — another home with the same answers, or a new model from scratch (ModelSomethingElse) — and the ways
 * on as links with icons (PlanPageLinks).
 */
import React from 'react';
import { ArrowLeftRight, BookOpen, Hammer, Pencil } from 'lucide-react';
import { formatCurrency } from '../../utils/index.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { STAMP_DUTY_STATES } from '../../../../shared/src/calculation/stampDuty.ts';
import AnnuityFamilyPanel from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { PlanOutOfDateBanner } from '../../components/strategy/PlanOutOfDateBanner.tsx';
import { ChosenPlanCard, DocumentAndNextStep, ModelSomethingElse, PlanPageLinks, type PlanFigure } from '../../components/strategy/ChosenPlanPage.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { dzPriceWords, type ChosenDzPlan } from './dzChosenPlan.ts';

export interface DzHubProps {
    factFind: DraftFactFind;
    plan: ChosenDzPlan;
    /** How many homes have a saved plan. */
    homeCount: number;
    onChangeSale: () => void;
    onAnotherHome: () => void;
    /** "A new model from scratch": step 1 again, the questions asked afresh (owner, 22 Oct 2026). */
    onStartFresh: () => void;
    onAllPlans: () => void;
    onExplain: () => void;
    onRisks: () => void;
    onAdvice: () => void;
    onBuild: () => void;
    /** "Build a plan yourself": a blank plan for the same sale (25 Oct 2026). */
    onBuildNew?: () => void;
    /** Why the chosen plan's figures are out of date (chosenPlanRefresh.ts), or null (25 Oct 2026). */
    outOfDate?: string | null;
    onGuide: () => void;
    onAssumptions: () => void;
    onHome: () => void;
}

const money = (n: number) => formatCurrency(Math.round(n));
const signed = (n: number) => `${n >= 0 ? '+' : '−'}${money(Math.abs(n))}`;
const dateWords = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : null);
const MOTIVE_WORDS: Record<string, string> = { money: 'to free up money', home: 'for a home that suits them better', both: 'for a better home and to free up money' };

const DzHub = ({ factFind, plan, homeCount, onChangeSale, onAnotherHome, onStartFresh, onAllPlans, onExplain, onRisks, onAdvice, onBuild, onBuildNew, outOfDate = null, onGuide, onAssumptions, onHome }: DzHubProps) => {
    const v = householdVoice(factFind);
    const card = plan.inputs!.card!;
    const m = card.move;
    const base = card.baseline;
    const delta = base ? card.plan.legacy - base.legacy : null;
    const yrs = `${card.horizonYears} ${card.horizonYears === 1 ? 'year' : 'years'}`;
    const chosenOn = dateWords(plan.createdAt);
    const cash = (n: number) => (n < 0 ? `short ${money(-n)}` : money(n));
    const stateName = STAMP_DUTY_STATES.find((s) => s.code === m.state)?.name;
    const when = m.date ? `Selling on ${dateWords(m.date)}` : 'Selling now';
    const motive = String(((factFind?.goals || {}) as { dzMotive?: string }).dzMotive || '');
    const link = 'font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900';
    const cf = card.plan.cashflow;
    const pensionChange = base ? card.plan.pension - base.pension : null;

    // The strip, as the tested plans' card says it (DzPlansView): the Age Pension, the year's cash, the money freed.
    const figures: PlanFigure[] = [
        {
            label: 'Age Pension', value: money(card.plan.pension), unit: 'a year', testId: 'dz-hub-pension',
            note: pensionChange != null && Math.abs(pensionChange) >= 1 ? `${signed(pensionChange)} a year vs staying put` : base ? 'the same as staying put' : undefined,
        },
        {
            // A shortfall is named as one, as aged care and support at home do (22 Oct 2026).
            label: cf < 0 ? 'Each year, short by' : 'Left over each year', value: money(Math.abs(cf)), tone: cf < 0 ? 'plain' : 'good', testId: 'dz-hub-cashflow',
            note: base ? `staying put: ${cash(base.cashflow)}` : cf < 0 ? 'covered by savings' : 'stays in the bank',
        },
        {
            label: 'Money freed up', value: money(m.freedCapital), testId: 'dz-hub-freed',
            note: m.sellAndRent ? 'renting: the whole sale, after costs' : m.freedCapital < 1 ? (m.buyUpShortfall >= 1 ? `the sale buys the new home; ${money(m.buyUpShortfall)} more from savings` : 'the sale buys the new home') : m.stampDuty > 0 ? 'after stamp duty' : 'no stamp duty in the figures',
        },
    ];

    return (
        <div className="space-y-5" data-testid="dz-hub">
            <div>
                <p className="text-[13px] text-slate-500"><button type="button" onClick={onHome} className="underline decoration-dotted underline-offset-2 hover:text-slate-800">Home</button> › Downsizing</p>
                <h1 className="mt-2 text-[32px] font-bold leading-tight tracking-tight text-brand-navy" data-testid="dz-hub-title">{v.you ? 'Your downsizing plan' : `${v.Possessive} downsizing plan`}</h1>
                <p className="mt-1 text-[15px] text-slate-600">
                    {when} for {money(m.salePrice)}{stateName ? `, in ${stateName}` : ''}{m.homeName ? ` — ${m.homeName}` : ''}.{' '}
                    <button type="button" onClick={onChangeSale} className={link} data-testid="dz-hub-change-sale">Change the sale</button>
                    {' · '}
                    <button type="button" onClick={onAnotherHome} className={link} data-testid="dz-hub-another-home">Model another home</button>
                    {' · '}
                    <button type="button" onClick={onStartFresh} className={link} data-testid="dz-hub-start-fresh-header">Start a new model from scratch</button>.
                </p>
                {(MOTIVE_WORDS[motive] || delta != null) && (
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-900">
                        {MOTIVE_WORDS[motive] && <><strong>Why {v.you ? "you're" : `${v.they} ${v.are}`} moving:</strong> {v.you ? MOTIVE_WORDS[motive]!.replace('them', 'you') : MOTIVE_WORDS[motive]}. </>}
                        {delta != null && (delta < 0
                            ? <>The move leaves the family {money(-delta)} less than staying put over about {yrs}{m.sellingCosts + m.stampDuty > 0 ? `, mostly the ${money(m.sellingCosts + m.stampDuty)} of selling costs and stamp duty` : ''}.</>
                            : <>The move leaves the family {money(delta)} more than staying put over about {yrs}.</>)}
                    </p>
                )}
            </div>

            {/* A chosen plan whose figures are out of date says so, with a way to choose again (25 Oct 2026). */}
            <PlanOutOfDateBanner reason={outOfDate} onChooseAgain={onAllPlans} testId="dz-hub-out-of-date" />
            <ChosenPlanCard
                testIdPrefix="dz-hub"
                chosenOn={chosenOn}
                builtByYou={card.origin === 'manual'}
                title={card.words.title}
                sub={card.words.sub}
                estate={card.plan.legacy}
                years={card.horizonYears}
                estateDelta={delta}
                baseWord="staying put"
                figures={figures}
                onExplain={onExplain}
                onRisks={onRisks}
                onAllPlans={onAllPlans}
            >
                {/* The plan's key facts: the move, as it was chosen. ("When the money runs out" is not on the saved
                    card, so the plan page does not say it — owner, 22 Oct 2026.) */}
                <p className="text-[14px] leading-relaxed text-slate-800" data-testid="dz-hub-move">
                    <strong>The move:</strong> the sale leaves {money(m.salePrice - m.sellingCosts)} after {money(m.sellingCosts)} of selling costs
                    {m.sellAndRent
                        ? <>; {v.they} rent instead{m.rentPerWeek > 0 ? <>, at {money(m.rentPerWeek)} a week</> : ', the rent not entered yet'}.</>
                        : <>; the new home{m.homeName ? `, ${m.homeName},` : ''} costs about {dzPriceWords(m.newHomePrice)}{m.stampDuty > 0 ? <>, with {money(m.stampDuty)} of stamp duty</> : ', with no stamp duty in the figures — no state chosen'}{m.buyUpShortfall >= 1 ? <>, and {money(m.buyUpShortfall)} of it comes from savings</> : null}.</>}
                    {m.sellingCosts + m.stampDuty > 0 && <> Stamp duty and selling costs: {money(m.sellingCosts + m.stampDuty)}, gone on the day.</>}
                    {base && <> Staying put leaves the family {money(base.legacy)}.</>}
                </p>
                {card.family && card.family.rows.length > 0 && (
                    <div className="mt-4">
                        <AnnuityFamilyPanel
                            factFind={factFind}
                            personName={card.family.personName}
                            amount={card.family.amount}
                            kind={card.family.kind}
                            incomeAYear={card.family.incomeAYear}
                            rows={null}
                            frozen={card.family.rows}
                            compareNote={card.family.compareNote}
                            horizonYears={card.horizonYears}
                            testId="dz-hub-family-panel"
                        />
                    </div>
                )}
            </ChosenPlanCard>

            <div data-testid="dz-hub-next">
                <DocumentAndNextStep
                    documentState={plan.soaSnapshot ? 'prepared' : 'not prepared yet'}
                    documentLine="The written record of this plan, the sale and the new home."
                    documentAction={<button type="button" onClick={onAdvice} className={link} data-testid="dz-hub-advice">{plan.soaSnapshot ? 'Open the advice document' : 'Get the advice document'}</button>}
                    nextTitle="tell Services Australia about the sale"
                    nextLine={m.sellAndRent ? `Within 14 days of settlement, with the rent ${v.they} will pay, so Rent Assistance is worked out.` : `Within 14 days of settlement. Money set aside for the new home is exempt for up to 24 months while ${v.they} buy.`}
                    nextAction={<button type="button" onClick={onHome} className={link} data-testid="dz-hub-action-plan">Full action plan on Home</button>}
                />
            </div>

            <ModelSomethingElse
                testId="dz-hub-model-something-else"
                options={[
                    {
                        title: 'Another home, same answers',
                        body: `Keeps the sale, the state and ${v.their} answers to the questions. Only the new home is new${homeCount > 1 ? ` (${homeCount} homes modelled so far)` : ''}.`,
                        action: 'Model another home', onClick: onAnotherHome, testId: 'dz-hub-another-home-model',
                    },
                    {
                        title: 'A new model from scratch',
                        body: 'Starts again at step 1 with nothing carried over, and asks the questions again so any of them can change.',
                        action: 'Start a new model from scratch', onClick: onStartFresh, testId: 'dz-hub-start-fresh',
                    },
                ]}
            />

            <PlanPageLinks
                links={[
                    { label: 'Change the sale or new home', icon: <Pencil size={14} />, onClick: onChangeSale },
                    { label: 'All the plans we tested', icon: <ArrowLeftRight size={14} />, onClick: onAllPlans },
                    // Two doors (owner, 25 Oct 2026): change this plan, or start a blank one.
                    { label: card.origin === 'manual' ? 'Change the plan you built' : 'Customise this plan', icon: <Hammer size={14} />, onClick: onBuild, testId: 'dz-hub-build' },
                    ...(onBuildNew ? [{ label: 'Build a plan yourself', icon: <Hammer size={14} />, onClick: onBuildNew, testId: 'dz-hub-build-new' }] : []),
                    { label: 'How downsizing works — the guide', icon: <BookOpen size={14} />, onClick: onGuide },
                    { label: 'Rates and assumptions', icon: <BookOpen size={14} />, onClick: onAssumptions },
                ]}
            />
        </div>
    );
};

export default DzHub;
